import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  PictureAsPdf as PdfIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [portfolioPdf, setPortfolioPdf] = useState<string | null>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  let slideshowHeight = 500;
  if (isXs) slideshowHeight = 200;
  else if (isSm) slideshowHeight = 300;

  useEffect(() => {
    fetchPortfolioPdf();
  }, []);

  const fetchPortfolioPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portfolio/pdf`);
      if (response.data.pdf) {
        setPortfolioPdf(response.data.pdf);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        // Error fetching portfolio PDF
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files?.[0] || pdfUploading) return;

    const file = e.target.files[0];
    if (!file.name.endsWith(".pdf")) {
      setSnackbar({
        open: true,
        message: "Please upload a PDF file",
        severity: "error",
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "File size exceeds 10MB limit",
        severity: "error",
      });
      return;
    }

    setPdfUploading(true);
    const formData = new FormData();
    formData.append("pdf", file, file.name);

    try {
      await axios.post(`${API_URL}/api/portfolio/pdf`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setSnackbar({
        open: true,
        message: "Portfolio PDF uploaded successfully",
        severity: "success",
      });
      fetchPortfolioPdf(); // Refresh the PDF link
    } catch (err) {
      let errorMessage = "Failed to upload PDF";
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || errorMessage;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setPdfUploading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handlePortfolioClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePdfDownload = () => {
    if (!portfolioPdf) return;

    const url = portfolioPdf.startsWith("http")
      ? portfolioPdf
      : `${API_URL}${portfolioPdf}`;

    window.open(url, "_blank");
    handleMenuClose();
  };

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setMobileOpen(open);
    };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow:
            "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            py: { xs: 1, sm: 1.5 },
            px: { xs: 2, sm: 3, md: 6 },
            minHeight: { xs: 64, sm: 72, md: 80 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 2 },
            }}
          >
            <Button
              component={RouterLink}
              to="/"
              title="返回首頁"
              sx={{
                p: 0,
                m: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "scale(1.02)",
                },
                transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Box
                component="img"
                src="/icon/icon.png"
                alt="CosyLab Logo"
                sx={{
                  height: { xs: 48, sm: 56, md: 64 },
                  width: "auto",
                  maxWidth: "100%",
                  maxHeight: { xs: 48, sm: 56, md: 64 },
                  display: "block",
                  p: 0,
                  m: 0,
                }}
              />
            </Button>
          </Box>

          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                onClick={toggleDrawer(true)}
                sx={{
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>

              <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    width: 280,
                    backgroundColor: "rgba(255, 255, 255, 0.98)",
                    backdropFilter: "blur(10px)",
                  },
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  role="presentation"
                  onClick={toggleDrawer(false)}
                  onKeyDown={toggleDrawer(false)}
                >
                  <Box
                    sx={{ p: 3, borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      選單
                    </Typography>
                  </Box>
                  <List sx={{ py: 2 }}>
                    <ListItem
                      component={RouterLink}
                      to="/about"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="關於我們"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                    <ListItem
                      component={RouterLink}
                      to="/classes"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="課程"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                    {portfolioPdf && (
                      <ListItem
                        onClick={handlePdfDownload}
                        sx={{
                          cursor: "pointer",
                          mx: 2,
                          borderRadius: 2,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: "rgba(45, 90, 39, 0.08)",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <PdfIcon sx={{ color: "error.main", fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="查看作品集 PDF"
                          primaryTypographyProps={{
                            sx: { fontWeight: 500, fontSize: "1rem" },
                          }}
                        />
                      </ListItem>
                    )}
                    <ListItem
                      component={RouterLink}
                      to="/school-program"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="學校方案"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                    <ListItem
                      component={RouterLink}
                      to="/community-program"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="企業及社區方案"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                    <ListItem
                      component={RouterLink}
                      to="/partners"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="合作機構"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                    <ListItem
                      component={RouterLink}
                      to="/contact"
                      sx={{
                        cursor: "pointer",
                        mx: 2,
                        borderRadius: 2,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(45, 90, 39, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="聯絡我們"
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, fontSize: "1rem" },
                        }}
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  {user && user.isOwner && (
                    <Box sx={{ px: 3, py: 2 }}>
                      <input
                        accept="application/pdf"
                        style={{ display: "none" }}
                        id="pdf-upload-mobile"
                        type="file"
                        onChange={handlePdfUpload}
                      />
                      <label htmlFor="pdf-upload-mobile">
                        <Button
                          variant="contained"
                          component="span"
                          disabled={pdfUploading}
                          startIcon={
                            pdfUploading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <UploadFileIcon />
                            )
                          }
                          fullWidth
                          sx={{
                            backgroundColor: "primary.main",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            py: 1.5,
                            borderRadius: 2,
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                            "& svg": {
                              fontSize: 18,
                            },
                          }}
                        >
                          {pdfUploading ? "上傳中..." : "上傳作品集 PDF"}
                        </Button>
                      </label>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      p: 3,
                    }}
                  >
                    <IconButton
                      color="inherit"
                      href="https://wa.me/852"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#25D366",
                        backgroundColor: "rgba(37, 211, 102, 0.1)",
                        "&:hover": {
                          backgroundColor: "rgba(37, 211, 102, 0.2)",
                        },
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      href="https://www.instagram.com/cosylab.craft/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#E4405F",
                        backgroundColor: "rgba(228, 64, 95, 0.1)",
                        "&:hover": {
                          backgroundColor: "rgba(228, 64, 95, 0.2)",
                        },
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {/* Navigation Links */}
              <Button
                color="inherit"
                component={RouterLink}
                to="/school-program"
                title="學校到校課程方案"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                學校方案
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/community-program"
                title="企業及社區工作坊方案"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                企業及社區方案
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/about"
                title="關於我們"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                關於我們
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/classes"
                title="課程列表"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                課程
              </Button>
              <Button
                color="inherit"
                onClick={handlePortfolioClick}
                startIcon={<PdfIcon />}
                title="查看作品集 PDF"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                工作坊目錄
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/partners"
                title="合作機構"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                合作機構
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/contact"
                title="聯絡我們"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "rgba(45, 90, 39, 0.08)",
                    color: "primary.main",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                聯絡我們
              </Button>

              {/* Social Media Icons */}
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <IconButton
                  color="inherit"
                  href="https://wa.me/85284378839"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="WhatsApp 聯絡我們"
                  sx={{
                    width: 44,
                    height: 44,
                    color: "#25D366",
                    backgroundColor: "rgba(37, 211, 102, 0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(37, 211, 102, 0.15)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <IconButton
                  color="inherit"
                  href="https://www.instagram.com/cosylab.craft/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram @cosylab.craft"
                  sx={{
                    width: 44,
                    height: 44,
                    color: "#E4405F",
                    backgroundColor: "rgba(228, 64, 95, 0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(228, 64, 95, 0.15)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Box>

              {/* User Section */}
              {user ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  >
                    歡迎, {user.email}
                  </Typography>
                  {user.isOwner && (
                    <Box>
                      <input
                        accept="application/pdf"
                        style={{ display: "none" }}
                        id="pdf-upload"
                        type="file"
                        onChange={handlePdfUpload}
                      />
                      <label htmlFor="pdf-upload">
                        <Button
                          variant="contained"
                          component="span"
                          disabled={pdfUploading}
                          startIcon={
                            pdfUploading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <UploadFileIcon />
                            )
                          }
                          sx={{
                            backgroundColor: "primary.main",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            px: 2.5,
                            py: 1,
                            borderRadius: 2,
                            "&:hover": {
                              backgroundColor: "primary.dark",
                              transform: "translateY(-1px)",
                            },
                            height: 40,
                            "& svg": {
                              fontSize: 18,
                            },
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {pdfUploading ? "上傳中..." : "上傳作品集"}
                        </Button>
                      </label>
                    </Box>
                  )}
                  <Button
                    color="inherit"
                    onClick={logout}
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      px: 2.5,
                      py: 1,
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        color: "text.primary",
                      },
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    登出
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  登入
                </Button>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Portfolio Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
            mt: 1,
            border: "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        {portfolioPdf ? (
          <MenuItem
            onClick={handlePdfDownload}
            sx={{
              px: 3,
              py: 2,
              "&:hover": {
                backgroundColor: "rgba(45, 90, 39, 0.08)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <PdfIcon sx={{ mr: 2, color: "error.main", fontSize: 20 }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              查看作品集 PDF
            </Typography>
          </MenuItem>
        ) : (
          <MenuItem
            disabled
            sx={{
              px: 3,
              py: 2,
              color: "text.disabled",
            }}
          >
            <PdfIcon sx={{ mr: 2, fontSize: 20 }} />
            <Typography sx={{ fontSize: "0.95rem" }}>暫無作品集 PDF</Typography>
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontWeight: 500,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Navigation;
